// src/features/tasks/components/TaskDetailsPage/TaskCompletionApprovalCard.jsx
import React, { useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  EditCalendar as BackdateIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { useCompletionApproval } from '../../hooks/useCompletionApproval';
import { useTaskPermissions } from '../../../auth/hooks/usePermissions';
import { useSnackbar } from '../../../../hooks/mui/useSnackBar';
import ProfilePicture from '../../../../components/common/ProfilePicture';

const STATUS_COLOR = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
};

function formatDay(value) {
  if (!value) return '—';
  const date = typeof value === 'string' ? parseISO(value) : value;
  return format(date, 'dd/MM/yyyy');
}

/**
 * Shows the adjusted completion date recorded on a task and, for reviewers,
 * the approve/reject actions. Rendered only when the task has a request.
 */
export default function TaskCompletionApprovalCard({ task, onReviewed, sx }) {
  const { t } = useTranslation(['tasks', 'common']);
  const { showSuccess, showError } = useSnackbar();
  const { canApproveCompletion } = useTaskPermissions();

  const {
    approval,
    loading,
    error,
    fetchApproval,
    approve,
    reject,
  } = useCompletionApproval();

  const taskId = task?.task_id;
  const approvalId = task?.completion_approval_id;

  useEffect(() => {
    if (taskId && approvalId) fetchApproval(taskId);
  }, [taskId, approvalId, fetchApproval]);

  if (!approvalId) return null;

  const handleReview = async (decision) => {
    const action = decision === 'approve' ? approve : reject;
    const result = await action(taskId);

    if (result.success) {
      showSuccess(t(`tasks:completion_approval.${decision}_success`));
      onReviewed?.();
    } else {
      showError(result.error || t(`tasks:completion_approval.${decision}_error`));
    }
  };

  const status = approval?.status;
  const isPending = status === 'PENDING';

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, ...sx }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <BackdateIcon color="action" />
          <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
            {t('tasks:completion_approval.title')}
          </Typography>
          {status && (
            <Chip
              size="small"
              color={STATUS_COLOR[status] ?? 'default'}
              label={t(`tasks:completion_approval.statuses.${status}`)}
            />
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading && !approval ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : approval ? (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mb: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('tasks:completion_approval.requested_date')}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatDay(approval.requested_completion_date)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('tasks:completion_approval.original_date')}
                </Typography>
                <Typography variant="body2">
                  {formatDay(approval.original_completion_date)}
                </Typography>
              </Box>
              {approval.requested_by && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('tasks:completion_approval.requested_by')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                    <ProfilePicture
                      profilePictureFileId={approval.requested_by.profile_picture_file_id}
                      name={approval.requested_by.name}
                      size={22}
                      imageSize="32x32"
                      color={approval.requested_by.color}
                    />
                    <Typography variant="body2">{approval.requested_by.name}</Typography>
                  </Box>
                </Box>
              )}
            </Box>

            <Typography variant="caption" color="text.secondary" display="block">
              {t('tasks:completion_approval.reason')}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {approval.reason}
            </Typography>

            {!isPending && approval.reviewed_by && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary">
                  {t('tasks:completion_approval.reviewed_by', {
                    name: approval.reviewed_by.name,
                    date: approval.reviewed_at ? formatDay(approval.reviewed_at) : '—',
                  })}
                </Typography>
              </>
            )}

            {isPending && canApproveCompletion && (
              <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ApproveIcon />}
                  disabled={loading}
                  onClick={() => handleReview('approve')}
                  sx={{ borderRadius: 2 }}
                >
                  {t('tasks:completion_approval.approve')}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<RejectIcon />}
                  disabled={loading}
                  onClick={() => handleReview('reject')}
                  sx={{ borderRadius: 2 }}
                >
                  {t('tasks:completion_approval.reject')}
                </Button>
              </Box>
            )}

            {isPending && !canApproveCompletion && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                {t('tasks:completion_approval.awaiting_admin')}
              </Alert>
            )}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
